import React from 'react';
import { UserCheck, Stethoscope, NotebookTabs } from 'lucide-react';

interface PatientBottomNavProps {
  activeCategory: 'patientProfile' | 'clinicalCare' | 'management';
  onCategoryChange: (category: 'patientProfile' | 'clinicalCare' | 'management') => void;
  hasPatient: boolean;
  sidebarCollapsed?: boolean;
  canReadCaseManagement?: boolean;
  canReadClinicalCare?: boolean;
  /** Phase 18U U7: field-mode tablets (768 px and wider) keep the bottom nav like phones */
  alwaysShow?: boolean;
}

const PatientBottomNav: React.FC<PatientBottomNavProps> = ({
  activeCategory,
  onCategoryChange,
  hasPatient,
  sidebarCollapsed = true,
  canReadCaseManagement = true,
  canReadClinicalCare = true,
  alwaysShow = false
}) => {
  // Don't show if no patient or if sidebar is expanded (not collapsed)
  if (!hasPatient || !sidebarCollapsed) {
    return null;
  }

  const navItems = [
    {
      id: 'patientProfile' as const,
      icon: UserCheck,
      label: 'Profile'
    },
    ...(canReadClinicalCare ? [{
      id: 'clinicalCare' as const,
      icon: Stethoscope,
      label: 'Clinical'
    }] : []),
    ...(canReadCaseManagement ? [{
      id: 'management' as const,
      icon: NotebookTabs,
      label: 'Case Mgmt'
    }] : [])
  ];

  return (
    <div className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around h-16 items-center ${alwaysShow ? '' : 'md:hidden'} z-50 safe-area-inset-bottom`}>
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeCategory === item.id;

        return (
          <button
            key={item.id}
            onClick={() => onCategoryChange(item.id)}
            className={`flex flex-col items-center justify-center w-full h-full transition-all ${
              isActive
                ? 'text-slate-700 bg-slate-100'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={24} strokeWidth={isActive ? 2.5 : 2} className={isActive ? 'text-slate-700' : ''} />
            <span className={`text-xs mt-1 ${isActive ? 'font-semibold' : 'font-medium'}`}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default PatientBottomNav;
